/**
 * Apparition au scroll des cartes produit (.product-grid__item) — page collection.
 * Pur CSS (transitions) + IntersectionObserver. Aucune dépendance externe.
 *
 * Fonctionnement
 * --------------
 * - Le masquage initial (opacity:0 + translateY) est porté par le CSS, actif
 *   UNIQUEMENT sous la classe `stagger-ready` posée sur <html> par le script
 *   inline de la section, AVANT le paint (anti-FOUC). Sans JS / reduced-motion :
 *   la classe n'est jamais posée → les cartes restent visibles.
 * - Quand une carte entre dans le viewport, on lui ajoute `.is-visible`. Les
 *   cartes qui entrent dans le même lot reçoivent un transition-delay croissant
 *   → elles apparaissent l'une après l'autre (effet stagger).
 *
 * Robustesse face à l'infinite-scroll
 * -----------------------------------
 * - Cartes AJOUTÉES (pagination) ou grille REMPLACÉE (filtres) : un
 *   MutationObserver sur `.theme-main` (conteneur stable) ré-observe les
 *   nouvelles cartes.
 * - RESTAURATION de scroll au retour : un IntersectionObserver ne fire qu'au
 *   FRANCHISSEMENT d'un seuil. Quand l'infinite-scroll « saute » au milieu de la
 *   liste, les cartes au-dessus du viewport ne traversent jamais le seuil →
 *   l'IO ne les voit jamais. On les révèle alors directement (revealAbove),
 *   au scroll et sur quelques timers couvrant la restauration progressive.
 */
(function () {
  "use strict";

  var ROOT = document.documentElement;
  // La classe n'est posée que si le JS tourne ET que le mouvement est accepté.
  if (!ROOT.classList.contains("stagger-ready")) return;

  var ITEM_SELECTOR =
    ".product-grid__item:not(.product-grid__item--hero):not(.product-grid__item--push)";
  var VISIBLE_CLASS = "is-visible";
  var STAGGER_STEP = 70; // ms entre deux cartes d'un même lot
  var STAGGER_MAX = 8; // plafond du décalage (reste réactif en scroll rapide)

  // Très vieux navigateur sans IO : on révèle tout, plutôt que risquer des
  // cartes invisibles.
  if (!("IntersectionObserver" in window)) {
    ROOT.classList.remove("stagger-ready");
    return;
  }

  // Désarme le filet de sécurité inline (cf. section) : l'animation est en place.
  window.__staggerReady = true;

  function show(el, delay) {
    el.style.setProperty("--stagger-delay", (delay || 0) + "ms");
    el.classList.add(VISIBLE_CLASS);
  }

  // Ordre DOM (gauche→droite, haut→bas) pour un stagger cohérent.
  function byDomOrder(a, b) {
    return a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1;
  }

  var io = new IntersectionObserver(
    function (entries, obs) {
      var entering = [];
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          obs.unobserve(entry.target);
          entering.push(entry.target);
        }
      });
      if (!entering.length) return;
      // Les cartes qui entrent ensemble sont décalées les unes après les autres.
      entering.sort(byDomOrder);
      entering.forEach(function (el, i) {
        show(el, Math.min(i, STAGGER_MAX) * STAGGER_STEP);
      });
    },
    { rootMargin: "0px 0px -10% 0px", threshold: 0.1 }
  );

  function observeAll() {
    var items = document.querySelectorAll(ITEM_SELECTOR + ":not(." + VISIBLE_CLASS + ")");
    for (var i = 0; i < items.length; i++) io.observe(items[i]);
  }

  // Révèle SANS décalage les cartes entièrement au-dessus du viewport : l'IO ne
  // les verra jamais traverser le seuil après un saut de scroll (restauration).
  function revealAbove() {
    var items = document.querySelectorAll(ITEM_SELECTOR + ":not(." + VISIBLE_CLASS + ")");
    for (var i = 0; i < items.length; i++) {
      var el = items[i];
      if (el.getBoundingClientRect().bottom <= 0) {
        io.unobserve(el);
        show(el, 0); // hors écran : le décalage n'aurait aucun intérêt visuel
      }
    }
  }

  // Throttle rAF : le scroll fire très souvent, revealAbove ne tourne qu'une
  // fois par frame.
  var queued = false;
  function onScroll() {
    if (queued) return;
    queued = true;
    requestAnimationFrame(function () {
      queued = false;
      revealAbove();
    });
  }

  observeAll();
  window.addEventListener("scroll", onScroll, { passive: true });
  // La restauration de scroll de l'infinite-scroll est progressive (images qui
  // chargent, corrections jusqu'à ~2,5 s). Quelques passes différées en filet,
  // sans danger car revealAbove est idempotente.
  [200, 600, 1200, 2600].forEach(function (d) {
    window.setTimeout(revealAbove, d);
  });

  // Cartes ajoutées (pagination) ou grille remplacée (filtres). `.theme-main`
  // est le conteneur stable qui survit aux deux. Les nouvelles cartes héritent
  // du masquage CSS (`stagger-ready` toujours présent) → pas de flash.
  var container = document.querySelector(".theme-main") || document.body;
  new MutationObserver(function (mutations) {
    for (var i = 0; i < mutations.length; i++) {
      if (mutations[i].addedNodes.length) {
        observeAll();
        return;
      }
    }
  }).observe(container, { childList: true, subtree: true });
})();
